/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { isCheckoutPage } from 'models/CheckoutPageModel';
import { BasePageModel } from 'models/BasePageModel';
import { PageState } from 'models/PageState';
import { formValueSelector } from 'redux-form';
import { AddressInfo } from 'models/AddressInfo';
import { CheckoutFormData } from 'models/CheckoutFormData';
import { CustomerType } from 'models/CustomerType';
import { SuggestedAddress } from 'models/SuggestedAddress';
import { ICart } from 'models/ICart';
import { AddressServiceType } from 'models/AddressServiceType';
import { ICharge } from 'models/ICharge';
import { Charge } from 'models/Charge';
import { hasElements } from 'utils';
import { Voucher } from 'models/Voucher';
import { CartItem } from 'models/CartItem';
import { getIsRentalProduct } from 'components/Shared/Price/selector';
import { AdminUserInfo } from 'authorization/models/AdminUserInfo';
import { SummaryItem } from 'models/SummaryItem';

export const checkoutFormName = 'checkout';

export const selectPhoneNumber = (state: PageState) => state.header.contact.phonenumber;

export const isVouchersCoveringExpenses = (cart: ICart): boolean =>
  cart.vouchers.length > 0 && cart.totalLineAmountIncludingCharges <= 0;

export const useFreeOfChargePaymentMethod = (cart: ICart): boolean => {
  const charges = cart.cartCharges.reduce((partialSum, a) => partialSum + a.originalAmount, 0);
  const totalOriginalAmount = cart.originalTotalPrice + charges;

  return cart.totalDiscountAmount >= totalOriginalAmount;
};

export const getVoucherTotal = (vouchers: Voucher[]): number =>
  hasElements(vouchers) ? vouchers.reduce((acc, { amount }) => acc + amount, 0) : 0;

export const getInitialFormValues = (currentPage: BasePageModel): CheckoutFormData =>
  isCheckoutPage(currentPage) ? currentPage.initialValues : ({} as CheckoutFormData);

export const getPurchaseValidationMessage = (currentPage: BasePageModel) =>
  (isCheckoutPage(currentPage) &&
    currentPage.purchaseResponse &&
    currentPage.purchaseResponse.message) ||
  '';

export const getPurchaseValid = (currentPage: BasePageModel) =>
  isCheckoutPage(currentPage) && currentPage.purchaseResponse && currentPage.purchaseResponse.valid;

export const isSingleProduct = ({ currentPage }: PageState) =>
  isCheckoutPage(currentPage) && currentPage.isSingleProduct;

export const getDeliveries = (state: PageState) =>
  isCheckoutPage(state.currentPage) ? state.currentPage.deliveries : [];

export const formatAddress = ({ street = '', streetNumber = '', letter = '' }: SuggestedAddress) =>
  `${street} ${streetNumber}${letter}`;
export const formatZipCode = ({ postalcode, locality }: SuggestedAddress) =>
  `${postalcode} ${locality}`;
export const fieldStreet = 'street';
export const fieldStreetAlternate = 'street_alternate';
export const fieldZipcode = 'zipcode';
export const fieldZipcodeAlternate = 'zipcode_alternate';

export const cartContainsProduct = (state: PageState) => state.cart.cartItems.length > 0;

export const getSingleProductItem = (state: PageState) =>
  isSingleProduct(state) && state.cart.cartItems[0];

export const checkoutForm = formValueSelector(checkoutFormName);

export const getCustomerType = (state: PageState) =>
  checkoutForm(state, 'address.customerType') as CustomerType;

type KeysType = Partial<keyof AddressInfo>[];
const commonKeys: KeysType = ['street', 'zipCode', 'city', 'phoneNumber', 'email'];
const customerKeys: { [key: number]: KeysType } = {
  [CustomerType.Private]: [...commonKeys, 'firstName', 'lastName'],
  [CustomerType.Company]: [...commonKeys, 'lastName', 'identityNumber']
};

const commonDeliveryKeys: KeysType = ['lastName', 'street', 'zipCode', 'city'];
const deliveryKeys: { [key: number]: KeysType } = {
  [CustomerType.Private]: [...commonDeliveryKeys, 'firstName'],
  [CustomerType.Company]: [...commonDeliveryKeys]
};

enum rotTypes {
  apartment,
  house
}

const rotKeys: { [key: number]: KeysType } = {
  [rotTypes.apartment]: ['identityNumber', 'apartmentOrganisationNumber', 'apartmentNumber'],
  [rotTypes.house]: ['identityNumber', 'cadastral']
};

const isInvalid = (
  keys: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: { [key: string]: any } = {},
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: { [key: string]: any } = {}
) => keys.some(k => !fields[k] || errors[k]);

type IsContactInfoInvalid = (
  address: AddressInfo,
  errors: Partial<CheckoutFormData>,
  needsCertificateId: boolean
) => boolean;

export const isContactInfoInvalid: IsContactInfoInvalid = (address, errors, needsCertificateId) => {
  if (!address) {
    return true;
  }

  const isInvalidAddress = address.hasDeliveryAddress
    ? isInvalid(customerKeys[address.customerType], address, errors.address) ||
      isInvalid(
        deliveryKeys[address.customerType],
        address.deliveryAddress,
        errors.address && errors.address.deliveryAddress
      )
    : isInvalid(customerKeys[address.customerType], address, errors.address);

  if (!needsCertificateId) {
    return isInvalidAddress;
  }

  return !address.confirmCertifiedInstallation || isInvalidAddress;
};

export const isRentalInfoInvalid = (cartItems: CartItem[]): boolean => {
  if (!cartItems) {
    return true;
  }

  return cartItems.some(
    cartItem =>
      getIsRentalProduct(cartItem) &&
      !cartItem.rentalPeriod?.pickupDate &&
      !cartItem.rentalPeriod?.returnDate
  );
};

export const isRotInformationInvalid = (
  address: AddressInfo,
  isRotDeduction: boolean,
  errors: Partial<CheckoutFormData>
): boolean => {
  if (!isRotDeduction) {
    return false;
  }
  if (!address) {
    return true;
  }
  if (errors.address) {
    if (errors.address?.cadastral) {
      return isInvalid(rotKeys[rotTypes.house], address, errors.address);
    } else {
      return isInvalid(rotKeys[rotTypes.apartment], address, errors.address);
    }
  } else return false;
};

const DEFAULT_ADDRESS_INFO = { zipCode: '', city: '', firstName: '', lastName: '', street: '' };
interface DeliveryAddressInfo {
  zipCode: string;
  city: string;
  name: string;
  street: string;
}
const toDeliveryAddressInfo = (addressInfo?: AddressInfo): DeliveryAddressInfo => {
  const { zipCode, city, firstName, lastName, street } = addressInfo
    ? addressInfo
    : DEFAULT_ADDRESS_INFO;

  return { zipCode, city, name: `${firstName} ${lastName || ''}`, street };
};

export const getAddressInfoForDelivery = (state: PageState): DeliveryAddressInfo => {
  const hasAlternateDelivery = checkoutForm(state, 'address.hasDeliveryAddress');

  return hasAlternateDelivery ? getAlternateAddressInfo(state) : getAddressInfo(state);
};

export const getTermsAccepted = (state: PageState): boolean => checkoutForm(state, 'acceptedTerms');

export const getDeliveryAddressInfo = (state: PageState) =>
  toDeliveryAddressInfo(getAddressInfo(state));

export const getAlternateDeliveryAddressInfo = (state: PageState) =>
  toDeliveryAddressInfo(getAlternateAddressInfo(state));

export const getAddressInfo = (state: PageState): AddressInfo => {
  return checkoutForm(state, 'address');
};

export const getAlternateAddressInfo = (state: PageState) => {
  return checkoutForm(state, 'address.deliveryAddress');
};

export const getAddressForDeliveryInformation = ({
  state,
  useDeliveryAddress
}: {
  state: PageState;
  useDeliveryAddress?: boolean;
}): Partial<AddressInfo> => {
  const base = useDeliveryAddress ? `address.deliveryAddress` : 'address';
  const selectorKeys = getSelectorKeys(state, base);
  const { address = {} } = checkoutForm(state, ...selectorKeys, 'address.hasDeliveryAddress');

  return useDeliveryAddress ? address.deliveryAddress || {} : address;
};

const getSelectorKeys = (state: PageState, base: string) => {
  const isCompany = getCustomerType(state);

  if (isCompany === CustomerType.Company) {
    return deliveryKeys[CustomerType.Company].map(key => `${base}.${key}`);
  }

  return deliveryKeys[CustomerType.Private].map(key => `${base}.${key}`);
};

export const isFetched = (form: Pick<CheckoutFormData, 'address'>) =>
  form.address.addressService > AddressServiceType.Manual;

export const filterCharges = (c: ICharge): c is Charge => !zipFreightCheck(c);
export const zipFreightCheck = (c: ICharge): boolean => 'zipCharge' in c;

export const getTotal = (state: PageState) => state.cart.totalLineAmountIncludingCharges;
export const getVouchers = (state: PageState) => state.cart.vouchers;
export const getDiscountCode = (state: PageState) => state.cart.discountCode;
export const getTotalDiscountAmount = (state: PageState) => state.cart.totalDiscountAmount;
export const getTotalDiscountPercent = (state: PageState) => state.cart.totalDiscountPercentString;

// Functions related to disable card-payment method on ROT-products with FeatureToggle
export const getShouldUseRotFeatureToggle = (state: PageState): boolean => {
  if (getDisableCardPaymentROTStatus(state.currentPage)) {
    return getHasRotDeduction(state);
  }
  return false;
};

export const getHasRotDeduction = (state: PageState) => state.cart.hasRotDeduction;

const getDisableCardPaymentROTStatus = (currentPage: BasePageModel): boolean =>
  isCheckoutPage(currentPage) && currentPage.disableCardPaymentROTFeatureToggle;

export const getIsChannelPolarPumpen = (state: PageState) => state.appShell.channel === 'PP';

export const selectShouldShowBHGNewsletter = (state: PageState): boolean =>
  state.appShell.shouldShowBHGNewsletter;

// Functions related to disable messageBox with FeatureToggle
export const selectDisableMessageBoxFeatureToggle = (state: PageState): boolean => {
  const status =
    isCheckoutPage(state.currentPage) && state.currentPage.removeCheckoutMessageBoxFeatureToggle;

  return status;
};

export const selectUserInfo = (state: PageState): AdminUserInfo | undefined =>
  isCheckoutPage(state.currentPage) ? state.currentPage.userInfo : undefined;

export const selectAdminCartCharges = (state: PageState): CartItem[] => state.cart.adminCartCharges;

export const selectSalesOriginId = (state: PageState): string => state.cart.salesOriginId;
export const selectSalesOriginClassId = (state: PageState): string => state.cart.salesOriginClassId;

export const selectSalesTaker = (state: PageState): string | undefined => state.cart.salesTaker;
export const selectInternalMessage = (state: PageState): string | undefined =>
  state.cart.internalMessage;
export const selectIsBankIdActive = (state: PageState): boolean =>
  isCheckoutPage(state.currentPage) && state.currentPage.isBankIdActive;

export const selectShouldUseIngrid = (state: PageState): boolean => state.appShell.shouldUseIngrid;

export const SelectFulfilledInSystem = (state: PageState): number => state.cart.fulfilledInSystem;

export const selectHasSummaryItems = (state: PageState): boolean =>
  isCheckoutPage(state.currentPage) && hasElements<SummaryItem>(state.currentPage.summary.items);
